import { t } from '@lingui/macro';

import * as help from 'helpers';
import { TRIAL_DISCOUNTS } from 'config';

const ACCESS_LOGIN_DESC = t`Experience everything Yousician Premium has to offer!`;
const ACCESS_SIGNUP_DESC = t`Experience everything Yousician Premium has to offer!`;

const SIGNUP = 'signup';
const LOGIN = 'login';

// decide between login or sign up forms, based on localStorage
const toggleDefaultForm = (): 'signup' | 'login' => {
    let userHasBeenHereBefore = false;
    try {
        // if common is found, means user has logged in before
        userHasBeenHereBefore = !!localStorage.getItem('common');
    } catch (e) {}

    if (userHasBeenHereBefore) {
        return LOGIN;
    }

    return SIGNUP;
};

const ACCESS = {
    default: {
        login: t`Log in`,
        signup: t`Sign up`,
        defaultForm: LOGIN,
        showBenefits: true,
    },

    subscribe: {
        login: t`Log in`,
        signup: t`Sign up`,
        defaultForm: LOGIN,
        showBenefits: true,
    },

    redeem: {
        login: t`Log in to redeem your coupon`,
        signup: t`Sign up to redeem your coupon`,
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'black-friday': {
        // as all dates are shown on client timezone
        // and we need to show countdowns
        // let's not manipulate them in code and show:

        // actual start of campaign
        campaign_starts: process.env.REACT_APP_E2E
            ? '2021-11-08'
            : '2021-11-12',
        // first day when campaign is not active
        campaign_ends: process.env.REACT_APP_E2E ? '2050-12-03' : '2021-12-01',
        login: t`Log in to get 50% off`,
        signup: t`Sign up to get 50% off`,
        defaultForm: toggleDefaultForm(),
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'partnership-jason': {
        campaign_starts: process.env.REACT_APP_E2E
            ? '2020-11-01'
            : '2021-08-22',
        campaign_ends: process.env.REACT_APP_E2E ? '2050-12-03' : '2021-09-27',
        login: t`Log in to get 25% off`,
        signup: t`Sign up to get 25% off`,
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'april-discount': {
        // On the page we show the end date one day before this, to count for all the time zones and etc.
        // e.g. if you set 21st in here, the page shows 20th
        campaign_starts: process.env.REACT_APP_E2E
            ? '2019-11-01'
            : '2021-04-11',
        campaign_ends: process.env.REACT_APP_E2E ? '2050-12-03' : '2021-06-30',
        login: t`Log in to get 50% off`,
        signup: t`Sign up to get 50% off`,
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'yousician-25': {
        // On the page we show the end date one day before this, to count for all the time zones and etc.
        // e.g. if you set 21st in here, the page shows 20th
        campaign_starts: '2020-05-22',
        campaign_ends: process.env.REACT_APP_E2E ? '2050-12-03' : '2020-05-28',
        login: t`Log in to get 25% off`,
        signup: t`Sign up to get 25% off`,
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    // both premium-offer and premium-plus offers are in fact premium-plus offers!
    //  for the corporation campaign  see documentation in src/pages/FreeTrialOffer/README.md
    'premium-offer': {
        // On the page we show the end date one day before this, to count for all the time zones and etc.
        // e.g. if you set 21st in here, the page shows 20th
        campaign_starts: '2020-05-10',
        // this campaign will be valid until second notice
        campaign_ends: '2050-12-03',
        login: t`Log in to get 35% off`,
        signup: t`Sign up to get 35% off`,
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },
    // both premium-offer and premium-plus offers are in fact premium-plus offers!
    //  for the corporation campaign  see src/pages/FreeTrialOffer/README.md
    'premium-plus-offer': {
        // On the page we show the end date one day before this, to count for all the time zones and etc.
        // e.g. if you set 21st in here, the page shows 20th
        campaign_starts: '2020-05-10',
        // this campaign will be valid until second notice
        campaign_ends: '2050-12-03',
        login: t`Log in to get 50% off`,
        signup: t`Sign up to get 50% off`,
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'world-music-day': {
        // On the page we show the end date one day before this, to count for all the time zones and etc.
        // e.g. if you set 21st in here, the page shows 20th
        campaign_starts: '2020-06-01',
        // this campaign will be valid until second notice
        campaign_ends: '2020-06-28',
        login: 'Log in to get 30% off',
        signup: 'Sign up to get 30% off',
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },
    'back-to-school': {
        campaign_starts: '2019-08-07',
        campaign_ends: '2019-09-03',
        login: t`Log in to get 50% off`,
        signup: t`Sign up to get 50% off`,
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'new-year': {
        campaign_starts: '2020-12-27',
        // On the page we show the end date one day before this, to count for all the time zones and etc.
        campaign_ends: '2021-01-21',
        login: t`Log in to get 30% off`,
        signup: t`Sign up to get 30% off`,
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'start-now': {
        login: t`Log in to get 25% off`,
        signup: t`Sign up to get 25% off`,
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'free-trial-offer': {
        login: t`Log in to start your 30-day free trial`,
        signup: t`Sign up to start your 30-day free trial`,
        defaultForm: toggleDefaultForm(),
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'free-trial-offer-7': {
        login: t`Log in to start your 7-day free trial`,
        signup: t`Sign up to start your 7-day free trial`,
        defaultForm: toggleDefaultForm(),
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'free-trial-offer-14': {
        login: t`Log in to start your 14-day free trial`,
        signup: t`Sign up to start your 14-day free trial`,
        defaultForm: toggleDefaultForm(),
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    'free-trial-login': {
        login: t`Log in to start your 7-day free trial`,
        signup: t`Sign up to start your 7-day free trial`,
        defaultForm: SIGNUP,
        showBenefits: true,
        descriptions: {
            signup: t`Sign up to experience everything Yousician Premium has to offer!`,
            login: t`Login to experience everything Yousician Premium has to offer!`,
        },
    },

    'start-trial': {
        login: () => {
            const params = help.useQueryParams();
            let discount = t`Log in to get your discount`;

            if (!params.continue) {
                return discount;
            }

            Object.keys(TRIAL_DISCOUNTS).forEach((token: any) => {
                if (
                    typeof token === 'string' &&
                    params &&
                    params.continue &&
                    params.continue.indexOf(token) >= 0
                ) {
                    discount = t`Log in to get ${TRIAL_DISCOUNTS[token].percentage}% off`;
                }
            });

            return discount;
        },
        signup: () => {
            const params = help.useQueryParams();
            let discount = t`Sign up to get your discount`;

            if (!params.continue) {
                return discount;
            }

            Object.keys(TRIAL_DISCOUNTS).forEach((token) => {
                if (
                    params &&
                    params.continue &&
                    params.continue.indexOf(token) >= 0
                ) {
                    discount = t`Sign up to get ${TRIAL_DISCOUNTS[token].percentage}% off`;
                }
            });

            return discount;
        },
        defaultForm: LOGIN,
        showBenefits: true,
        descriptions: {
            signup: ACCESS_SIGNUP_DESC,
            login: ACCESS_LOGIN_DESC,
        },
    },

    gifting: {
        login: t`Log in`,
        signup: t`Sign up`,
        defaultForm: toggleDefaultForm(),
        showBenefits: false,
        descriptions: {
            signup: t`Please create an account to order your gift card`,
            login: t`Please login to order your gift card`,
        },
    },

    zendesk: {
        login: t`Log in`,
        signup: t`Sign up`,
        defaultForm: LOGIN,
        showBenefits: false,
        descriptions: {
            signup: t`Please create an account to access the help hub`,
            login: t`Please login to access the help hub`,
        },
    },

    'gifting-redeem': {
        login: t`Log in`,
        signup: t`Sign up`,
        defaultForm: toggleDefaultForm(),
        showBenefits: true,
        descriptions: {
            signup: t`Sign up to redeem your gift card`,
            login: t`Log in to redeem your gift card`,
        },
    },
};

export default ACCESS;
